import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "./auth-store.js";
import {
  SessionUnavailableScreen,
  SignInElsewhereScreen,
  SignInStartFailureScreen,
} from "./auth-screens.js";
import { useSignInStart } from "./sign-in-start.js";

interface RequireAuthProps {
  readonly children: ReactNode;
}

/**
 * Route gate. Renders the children when the user is authenticated. When the
 * BFF reports "unauthenticated" (no session cookie or expired), this
 * component asks the BFF to mint an OIDC login URL and navigates to it.
 *
 * The BFF returns the URL in a JSON body (not a 302 redirect) so the SPA's
 * Set-Cookie semantics around the cross-origin redirect remain on the
 * SPA's origin — see [oidc.cookies.ts] for the browser-binding cookie that
 * the BFF sets at this point.
 *
 * `beginLogin` keeps this page's path in the tab (`pb.auth.returnTo`) before
 * it asks the BFF, so a sign-in the return page has to restart lands back
 * here rather than on Home (PB-5461, d2).
 *
 * Only one tab signs in at a time (PB-5461, d21). The start is automatic, so
 * it first elects a leader tab; the leader goes on as above, and every other
 * tab says it is waiting, keeps asking /me, and shows its page as soon as the
 * leader's session exists — it never left, so its address is its own page.
 *
 * `startedRef` guards against React 18 StrictMode firing the effect twice
 * in dev — the same idempotency we had with the prior react-oidc-context
 * adapter, kept because the new flow is no less re-entrant. It also keeps the
 * election to one per attempt.
 *
 * A `/me` that FAILS (5xx, 429, unparseable body, dead connection) is not a
 * sign-out and must never start a login — see PB-4900, and the specs that pin
 * it. It lands on `unavailable` instead, which is the one state this
 * component can neither resolve nor wait out, so it gets a button (PB-5206).
 * While `/me` is still being asked, the screen says so: "Redirecting to sign
 * in…" is shown only once a sign-in is actually being started.
 */
export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  const startedRef = useRef(false);
  const signIn = useSignInStart();
  const { start, startAutomatic, stopWaiting } = signIn;
  const path = location.pathname + location.search;

  useEffect(() => {
    // A session that arrives (another tab signed in) ends this attempt, so a
    // later expiry starts a fresh one rather than finding the guard spent.
    if (status === "authenticated") {
      startedRef.current = false;
      stopWaiting();
    }
    if (status !== "unauthenticated") return;
    if (startedRef.current) return;
    startedRef.current = true;
    startAutomatic(path);
  }, [status, path, startAutomatic, stopWaiting]);

  if (status === "authenticated") {
    return <>{children}</>;
  }

  if (status === "unavailable") {
    return <SessionUnavailableScreen />;
  }

  if (signIn.failure) {
    return (
      <SignInStartFailureScreen
        failure={signIn.failure}
        onTryAgain={signIn.retry}
      />
    );
  }

  if (signIn.waiting) {
    return (
      <SignInElsewhereScreen
        onSignedIn={stopWaiting}
        onLeaseFree={() => startAutomatic(path)}
        onSignInHere={() => start(path)}
      />
    );
  }

  /* Kept as plain text: a Spinner ring beside it is a look change no PR 3 row
     approves (OK-38 names other files). */
  return (
    <div className="flex h-screen items-center justify-center text-pb-text-secondary">
      <p>
        {status === "unknown"
          ? "Checking your session…"
          : "Redirecting to sign in…"}
      </p>
    </div>
  );
}
